/**
 * Game Reset — regenerate terrain and reset all players for a new round.
 */

import { PHYSICS, TURN_FLOW, WORLD, GRAPPLE, VOID, ROUND_START, TRAINING, TARGET_BLAST_INTRO, ASCENT, SURVIVAL } from '../constants';
import { isSoloMode, modeIsAscent, modeIsRoguelite } from './mode';
import { getAllTankIds, lookupTank } from '../tanks';
import { generateRogueliteTerrain } from '../roguelite/zone-generator';
import type { GameState } from '../types';
import type { GameEffect } from './effects';
import { transitionPhase } from './effects';
import { generateTerrain } from '../terrain/generation';
import { getGroundY, getGroundYBelow, resetScorchMap } from '../terrain/grid';
import { resetSmoothing, initSmoothing } from '../terrain/sand-physics';
import { rollNewWind } from './wind';
import { getActiveLevel, loadLevel } from './level-loader';
import { loadBestTimes } from './best-times';
import { resetPlayerForRound } from './player-defaults';

export function resetGame(state: GameState, seed?: number): GameEffect[] {
  const fx: GameEffect[] = [];

  let spawnXs: number[];
  let spawnYs: number[] | undefined;
  const customLevel = getActiveLevel();

  if (modeIsRoguelite(state.modeState.mode)) {
    // Solo roguelite — 1 human player, no enemies (enemy spawns deferred to future 4P infra)
    const humanTankId = state.players[0]?.tankId ?? 'mortar';
    const rog = generateRogueliteTerrain(humanTankId, seed, 1);
    spawnXs = rog.spawnXs;
    resetScorchMap();
    fx.push({ type: 'terrain_rebuild' });
    state.terrainSeed = rog.seed;
    state.terrainBiome = rog.biomeDisplayName;
    state.terrainFeature = rog.featureDisplayName;
    state.stageLeft = rog.stageLeft;
    state.stageRight = rog.stageRight;
    state.modeState.rogueliteCheckpointYs = rog.checkpointYs;
    state.modeState.rogueliteEnemyCount = 0;

    // Keep players array at 1 (solo)
    state.players.length = 1;
    state.players[0].dormant = false;
    state.players[0].isAI = false;
    state.players[0].name = 'P1';
  } else if (customLevel) {
    // Reload the same custom level instead of regenerating
    const loaded = loadLevel(customLevel);
    spawnXs = loaded.spawnXs.slice(0, state.players.length);
    spawnYs = loaded.spawnYs.slice(0, state.players.length);
    resetScorchMap();
    fx.push({ type: 'terrain_rebuild' });
    state.terrainSeed = 0;
    state.terrainBiome = customLevel.name;
    state.terrainFeature = null;
    state.stageLeft = loaded.stageLeft;
    state.stageRight = loaded.stageRight;
    if (customLevel.stageId) state.stageId = customLevel.stageId;
  } else {
    // Normal procedural generation (or fixed stage if stageId is set)
    const forcedArchetype = modeIsAscent(state.modeState.mode) ? 'ascent' as const : undefined;
    const stageId = !forcedArchetype ? state.stageId : undefined;
    const terrainResult = generateTerrain(state.players.length, seed, forcedArchetype, stageId);
    spawnXs = terrainResult.spawnXs;
    spawnYs = terrainResult.spawnYs;
    resetScorchMap();
    fx.push({ type: 'terrain_rebuild' });
    state.terrainSeed = terrainResult.seed;
    state.terrainBiome = terrainResult.biomeDisplayName;
    state.terrainFeature = terrainResult.featureDisplayName;
    state.stageLeft = terrainResult.stageLeft;
    state.stageRight = terrainResult.stageRight;
  }

  // Update stage theme for renderers (stage colors must be set before terrain_rebuild renders)
  if (state.stageId) {
    fx.unshift({ type: 'stage_theme_change', stageId: state.stageId });
  }

  // Reset post-crater smoothing
  resetSmoothing(state);
  initSmoothing();

  // Reset all players
  for (let i = 0; i < state.players.length; i++) {
    const p = state.players[i];

    // Roguelite AI enemies: already positioned by zone generator, just reset state in-place
    if (modeIsRoguelite(state.modeState.mode) && p.isAI) {
      resetPlayerForRound(p, p.x, p.y);
      continue;
    }

    const sx = spawnXs[i];
    let spawnX = sx;
    let spawnY: number;

    // Use editor Y if available (baked presets / custom levels)
    if (spawnYs && spawnYs[i] != null) {
      const surfaceY = getGroundYBelow(sx, spawnYs[i] - 20);
      spawnY = surfaceY - PHYSICS.ECB_RADIUS;
      if (import.meta.env.DEV) console.log(`[SPAWN] P${i} sx=${sx} editorY=${spawnYs[i]} surfaceY=${surfaceY} spawnY=${spawnY}`);
    } else {
      const groundY = getGroundY(sx);
      if (import.meta.env.DEV) console.log(`[SPAWN] P${i} sx=${sx} groundY=${groundY} voidY=${modeIsAscent(state.modeState.mode) ? ASCENT.VOID_INITIAL_Y : 'std'} worldH=${WORLD.VIRTUAL_HEIGHT}`);
      // Safety: if getGroundY returns sentinel (no terrain), scan nearby columns
      if (groundY >= WORLD.VIRTUAL_HEIGHT - 50) {
        let found = groundY;
        for (let d = 1; d < 400; d++) {
          const leftY = getGroundY(sx - d);
          if (leftY < WORLD.VIRTUAL_HEIGHT - 50) { found = leftY; spawnX = sx - d; break; }
          const rightY = getGroundY(sx + d);
          if (rightY < WORLD.VIRTUAL_HEIGHT - 50) { found = rightY; spawnX = sx + d; break; }
        }
        spawnY = found - PHYSICS.ECB_RADIUS;
      } else {
        spawnY = groundY - PHYSICS.ECB_RADIUS;
      }
    }
    resetPlayerForRound(p, spawnX, spawnY);
  }

  // Reset state
  state.roundNumber++;
  // Multiplayer: alternate who goes first each round (P0 odd rounds, P1 even rounds)
  state.currentPlayer = (state.multiplayer?.enabled && state.roundNumber % 2 === 0) ? 1 : 0;
  state.startingPlayer = state.currentPlayer;
  transitionPhase(state, 'round_start', fx);
  state.roundStartTimer = ROUND_START.DURATION;
  state.roundStartFireReleased = false;
  state.projectiles = [];
  state.deferredSpawns = [];
  state.nextProjectileId = 1;
  state.firingPhaseKnockedPlayers = [];
  state.turnCount = 0;
  state.turnTimer = 0;
  state.freezeUntil = 0;
  state.frameCount = 0;
  state.hashCheckpointId = 0;
  state.winner = null;
  state.gameOverTimer = 0;
  state.turnNumber = 1;
  state.wind = rollNewWind();
  state.windShiftPending = false;
  state.windShiftTimer = 0;
  if (modeIsAscent(state.modeState.mode)) {
    state.voidY = ASCENT.VOID_INITIAL_Y;
    state.voidTargetY = ASCENT.VOID_INITIAL_Y;
    state.voidRiseStartY = ASCENT.VOID_INITIAL_Y;
    state.voidLastErodedY = WORLD.VIRTUAL_HEIGHT;
  } else {
    state.voidY = WORLD.VIRTUAL_HEIGHT - VOID.INITIAL_Y_OFFSET;
    state.voidTargetY = WORLD.VIRTUAL_HEIGHT - VOID.INITIAL_Y_OFFSET;
    state.voidRiseStartY = WORLD.VIRTUAL_HEIGHT - VOID.INITIAL_Y_OFFSET;
    state.voidLastErodedY = WORLD.VIRTUAL_HEIGHT;
  }
  state.voidRisePending = false;
  state.voidRiseTimer = 0;
  state.voidErosionCollapseNeeded = false;
  state.turnCountdown = 0;
  state.voidRetaliationQueue = [];
  state.voidRetaliationActive = false;
  state.voidMood = 0;
  state.voidMealCount = 0;
  state.voidMealX = 0;
  // Reset grapple
  state.grapple.active = null;
  if (modeIsRoguelite(state.modeState.mode)) {
    const ms = state.modeState;
    state.grapple.ammo = state.players.map((p) =>
      !p.isAI && ms.rogueliteHasGrapple ? (3 + ms.rogueliteGrappleBonus) : 0
    );
  } else {
    state.grapple.ammo = state.players.map(() => GRAPPLE.STARTING_AMMO);
  }
  // Reset vortex holes + collapse state
  state.vortexHoles = [];
  state.collapseActive = false;
  state.collapseTimer = 0;
  state.collapsePortals = [];
  state.beamActive = false;
  state.beamTimer = 0;
  state.beamPath = null;
  state.beamDisplayTimer = 0;
  state.beamPortalDetonations = [];
  // Reset DI
  state.diActivePlayer = -1;
  state.diWindowTimer = 0;
  state.diHitQueue = [];
  state.diWaitForAuthority = false;
  // Reset Burst
  state.burstActivePlayer = -1;
  state.burstWindowTimer = 0;
  state.burstWaitForAuthority = false;
  state.burstFromSpecial = false;
  state.burstFromDefense = false;
  // Reset Defense
  state.defenseQueue = [];
  state.defenseActivePlayer = -1;
  state.defensePreFreezeStates = new Map();
  state.defenseWaitForAuthority = false;
  // Edgeguard
  state.edgeguardChargeFrame = 0;
  state.edgeguardChargeTarget = 0;
  state.edgeguardFired = false;
  state.edgeguardKnockedIndices = [];
  state.edgeguardBeam = null;
  // Lottery
  state.lotterySlots = [];
  state.lotteryTimer = 0;
  state.lotteryLockIndex = 0;
  state.lotteryPickTimeout = 0;
  fx.push({ type: 'lottery_hide' });
  // Trick scoring (reset per round)
  state.trickSession = null;
  state.trickScores = Array(state.players.length).fill(0);
  state.missStreak = Array(state.players.length).fill(0);
  // Match stats (reset per round)
  const pc = state.players.length;
  state.matchStats = {
    damageDealt: Array(pc).fill(0),
    damageTaken: Array(pc).fill(0),
    bestSingleHit: Array(pc).fill(0),
    shotsFired: Array(pc).fill(0),
    voidKills: Array(pc).fill(0),
    skyKills: Array(pc).fill(0),
    kills: Array(pc).fill(0),
    bestCombo: Array(pc).fill(0),
    bestTurnScore: Array(pc).fill(0),
    totalTrickScore: Array(pc).fill(0),
  };
  state.postMatchReady = false;
  state.postMatchCursor = 0;
  state.coinsAwarded = false;
  state.activeGlitch = 0;

  // Mode-specific setup
  if (state.modeState.mode === 'training') {
    // Training: P2 is the dummy target — neutral gray, positioned at stage center-right
    if (state.players.length >= 2) {
      const dummy = state.players[1];
      dummy.name = 'DUMMY';
      dummy.color = TRAINING.DUMMY_COLOR;
      dummy.damagePercent = 0;
      // Reset mode-specific tracking
      state.modeState.dummyX = dummy.x;
      state.modeState.dummyY = dummy.y;
      state.modeState.dummyDamage = 0;
    }
  } else if (state.modeState.mode === 'tutorial') {
    // Tutorial: P2 is the dummy — capture spawn position for respawn
    if (state.players.length >= 2) {
      const dummy = state.players[1];
      dummy.name = 'DUMMY';
      dummy.color = TRAINING.DUMMY_COLOR;
      dummy.damagePercent = 0;
      state.modeState.dummyX = dummy.x;
      state.modeState.dummyY = dummy.y;
    }
  } else if (state.modeState.mode === 'target_blast') {
    // Target Blast: reset shot/time counters
    state.modeState.shotsFired = 0;
    state.modeState.elapsedTime = 0;
    state.modeState.allTargetsHit = false;
    state.modeState.exitGateActive = false;
    state.modeState.runComplete = false;
    state.modeState.bestTimes = loadBestTimes();

    // Intro sequence — starts at step 0
    state.modeState.introStep = 0;
    state.modeState.introTimer = TARGET_BLAST_INTRO.STEPS[0].duration;

    // Hide P2 — mark dead, move off-screen (keep in array, too many systems assume 2 players)
    if (state.players.length >= 2) {
      const p2 = state.players[1];
      // Capture P2 spawn position as exit gate location BEFORE hiding
      state.modeState.exitGateX = p2.x;
      state.modeState.exitGateY = p2.y;
      // Kill and move off-screen
      p2.alive = false;
      p2.pendingDeath = false;
      p2.x = -9999;
      p2.y = -9999;
      p2.actionState = { tag: 'dead' };
    }

    // Generate targets if none exist (procedural placement on terrain)
    if (state.modeState.targets.length === 0) {
      const targets: { x: number; y: number; radius: number; hit: boolean }[] = [];
      const stageWidth = state.stageRight - state.stageLeft;
      const numTargets = 5 + Math.floor(Math.random() * 4); // 5-8 targets

      for (let t = 0; t < numTargets; t++) {
        // Spread targets across the stage width
        const tx = state.stageLeft + stageWidth * (0.15 + 0.7 * (t / (numTargets - 1)));
        // Place at varying heights — some on terrain, some in the air
        const groundY = getGroundY(tx);
        const heightOffset = 40 + Math.random() * 200; // 40-240px above ground
        const ty = groundY - PHYSICS.ECB_RADIUS - heightOffset;

        targets.push({
          x: tx,
          y: Math.max(100, ty), // keep above sky
          radius: 20,
          hit: false,
        });
      }
      state.modeState.targets = targets;
    } else {
      // Reload existing targets — mark all as not hit
      for (const t of state.modeState.targets) {
        t.hit = false;
      }
    }

    // Emit rebuild so target renderer creates visuals
    fx.push({ type: 'targets_rebuild', targets: state.modeState.targets });
  } else if (state.modeState.mode === 'survival') {
    // Survival: P2 = random AI enemy at starting difficulty
    if (state.players.length >= 2) {
      const enemy = state.players[1];
      const tanks = getAllTankIds();
      const humanTankId = state.players[0].tankId;
      // Pick a random tank different from the human's
      let enemyTankId = tanks[Math.floor(Math.random() * tanks.length)];
      while (enemyTankId === humanTankId && tanks.length > 1) {
        enemyTankId = tanks[Math.floor(Math.random() * tanks.length)];
      }
      enemy.tankId = enemyTankId;
      enemy.color = lookupTank(enemyTankId).visual.bodyColor;
      enemy.name = 'ENEMY';
      enemy.isAI = true;
      enemy.aiDifficulty = SURVIVAL.DIFFICULTY_TABLE[0];
    }
    // Reset survival state
    state.modeState.survivalKills = 0;
    state.modeState.survivalSpawnTimer = 0;
    // Load high score from localStorage
    try {
      const stored = localStorage.getItem(SURVIVAL.HIGH_SCORE_KEY);
      if (stored) state.modeState.survivalHighScore = JSON.parse(stored);
    } catch { /* ignore */ }
  }

  return fx;
}
